/* app/bi-modals.jsx - BI core modal set.
   AddEdit manual entry is wired to /api/bi/entities (create/edit); the
   fetch-from-URL + Grow-from cross-fetch AI flows remain mock (sub-step 2b).
   Built with bi-* class families. */
import { api } from '../lib/api.js';
(function () {
  const { useState, useRef, useEffect } = React;
  function I(props) { return React.createElement(window.MlIcon, props); }
  // prototype relationship key ↔ backend relationships_to_me value
  const REL_OUT = { alt: "alternative", look: "lookalike", comp: "complementary" };
  const REL_IN = { alternative: "alt", lookalike: "look", complementary: "comp" };
  const pct = (c) => (c == null ? 0 : Math.round(c <= 1 ? c * 100 : c));
  const siteIdOf = (site) => (site && (site.site_id != null ? site.site_id : site.id));

  const REL = [["alt", "Alternative", "alt", "Solves the same problem a different way"], ["look", "Lookalike", "look", "A close, direct equivalent"], ["comp", "Complementary", "comp", "Pairs with what you offer"]];

  function Modal({ title, sub, wide, sm, onClose, children, foot }) {
    return (
      <div className="bi-scrim" onMouseDown={ev => { if (ev.target === ev.currentTarget) onClose(); }}>
        <div className={"bi-modal" + (wide ? " bi-modal--wide" : "") + (sm ? " bi-modal--sm" : "")} role="dialog" aria-modal="true">
          <div className="bi-modal__head">
            <div><div className="bi-modal__title">{title}</div>{sub && <div className="bi-modal__sub">{sub}</div>}</div>
            <button className="bi-iconbtn bi-modal__close" onClick={onClose} aria-label="Close"><I name="x" size={18} /></button>
          </div>
          {children}
          {foot && <div className="bi-modal__foot">{foot}</div>}
        </div>
      </div>
    );
  }

  function FetchProgress({ step, labels }) {
    return (
      <div className="bi-fetch-progress">
        {labels.map((l, i) => (
          <div className={"bi-fetch-step" + (i === step ? " bi-fetch-step--active" : i < step ? " bi-fetch-step--done" : "")} key={i}>
            <span className="bi-fetch-step__dot">{i < step ? <I name="check" size={14} /> : i === step ? <span className="ml-spinner ml-spinner--sm ml-spinner--ai" style={{ width: 14, height: 14 }} /> : i + 1}</span>
            <span className="bi-fetch-step__label">{l}</span>
          </div>
        ))}
      </div>
    );
  }

  function RelPills({ active, onToggle }) {
    return <div className="bi-proposed-card__pills" onClick={e => e.stopPropagation()}>{REL.map(([id, label]) => <button key={id} className={"bi-rel-pill bi-rel-pill--" + id + (active.includes(id) ? " on" : "")} onClick={() => onToggle(id)}>{label}</button>)}</div>;
  }

  function ProposedCard({ p, sel, onToggle, onRel }) {
    return (
      <div className={"bi-proposed-card" + (sel ? " bi-proposed-card--on" : "")} onClick={onToggle}>
        <label className="hds-check" onClick={e => e.stopPropagation()}><input type="checkbox" checked={sel} onChange={onToggle} /><span className="hds-check__box"><I name="check" size={12} /></span></label>
        <div className="bi-proposed-card__main">
          <div className="bi-proposed-card__name">{p.name}{p.cat && <span className="bi-cat-tag">{p.cat}</span>}</div>
          {p.url && <div className="bi-proposed-card__url">{p.url}</div>}
          <div className="bi-proposed-card__sum">{p.sum}</div>
          <RelPills active={p.rels} onToggle={(r) => onRel(p.id, r)} />
        </div>
        <div className="bi-conf">{p.conf}%</div>
      </div>
    );
  }

  // ---- Add / Edit modal (1I) ----
  function AddEditModal({ cfg, edit, onClose, site, onSaved }) {
    const DS = window.HuddleDesignSystem_775113;
    const { Input, Select } = DS;
    const siteId = site && (site.site_id != null ? site.site_id : site.id);
    const [tab, setTab] = useState("manual");
    const [name, setName] = useState(edit ? (edit.name || "") : "");
    const [url, setUrl] = useState(edit ? (edit.url || "") : "");
    const [rels, setRels] = useState(edit && edit.rels && edit.rels.length ? edit.rels : ["alt"]);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);
    const saveManual = async () => {
      if (!name.trim() || siteId == null) { if (siteId == null) setErr("No active site."); return; }
      setSaving(true); setErr(null);
      const payload = {
        site_id: siteId, entity_type: cfg.noun, name: name.trim(),
        url: cfg.hasUrl ? (url.trim() || null) : null,
        data: { relationships_to_me: rels.map(r => REL_OUT[r]).filter(Boolean) },
        source: "manual", validation_status: "validated",
      };
      try {
        if (edit) await api(`/bi/entities/${edit.id}`, { method: "PATCH", body: JSON.stringify(payload) });
        else await api(`/bi/entities`, { method: "POST", body: JSON.stringify(payload) });
        onSaved && onSaved();
        onClose();
      } catch (e) { setErr(e.message || "Save failed"); setSaving(false); }
    };
    const [phase, setPhase] = useState("idle"); // idle | running | results
    const [step, setStep] = useState(0);
    const [props, setProps] = useState([
      { id: 1, name: "Frase", url: "frase.io", sum: "AI content briefs and optimization. Overlaps with your content workflow.", conf: 82, rels: ["alt"] },
      { id: 2, name: "Dashword", url: "dashword.com", sum: "Content optimization and reporting for teams.", conf: 71, rels: ["alt"] },
      { id: 3, name: "Outranking", url: "outranking.io", sum: "SERP-driven content generation.", conf: 58, rels: ["look"] },
    ]);
    const [sel, setSel] = useState([1, 2]);
    const t = useRef(null);
    useEffect(() => () => clearInterval(t.current), []);
    const labels = ["Fetching the page", "Extracting profile with AI", "Proposing related entities"];
    const runFetch = () => {
      setPhase("running"); setStep(0);
      t.current = setInterval(() => setStep(s => { if (s >= 2) { clearInterval(t.current); setTimeout(() => setPhase("results"), 500); return s; } return s + 1; }), 850);
    };
    const toggleRel = (r) => setRels(s => s.includes(r) ? s.filter(x => x !== r) : [...s, r]);
    const toggleSel = (id) => setSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
    const cardRel = (id, r) => setProps(ps => ps.map(p => p.id === id ? { ...p, rels: p.rels.includes(r) ? p.rels.filter(x => x !== r) : [...p.rels, r] } : p));

    const foot = phase === "results"
      ? <><button className="bi-btn" onClick={onClose}>Cancel</button><button className="bi-btn bi-btn--primary" onClick={onClose}><I name="check" size={16} />Validate &amp; save ({sel.length + 1})</button></>
      : tab === "manual"
        ? <><button className="bi-btn" onClick={onClose}>Cancel</button><button className="bi-btn bi-btn--primary" disabled={saving || !name.trim()} onClick={saveManual}>{saving ? "Saving…" : "Save"}</button></>
        : <><button className="bi-btn" onClick={onClose}>Cancel</button><button className="bi-btn bi-btn--ai" disabled={phase === "running"} onClick={runFetch}><I name="sparkles" size={16} />Fetch &amp; analyze</button></>;

    return (
      <Modal title={edit ? "Edit " + cfg.noun : "Add a " + cfg.noun} onClose={onClose} foot={foot}>
        {!edit && phase !== "results" && (
          <div className="bi-modal__tabs"><div className="hds-tabs">
            <button className={"hds-tab " + (tab === "manual" ? "hds-tab--active" : "")} onClick={() => setTab("manual")}>Manual entry</button>
            <button className={"hds-tab " + (tab === "fetch" ? "hds-tab--active" : "")} onClick={() => setTab("fetch")}><I name="sparkles" size={15} />Fetch from URL</button>
          </div></div>
        )}
        <div className="bi-modal__body">
          {tab === "manual" && phase !== "results" && (
            <div className="bi-form-grid">
              <div><label className="bi-field__label">Name <span className="bi-field__req">*</span></label><Input value={name} onChange={e => setName(e.target.value)} placeholder={"e.g. " + (cfg.title === "Competitors" ? "Ahrefs" : "Acme Co")} /></div>
              {cfg.hasUrl && <div><label className="bi-field__label">Website</label><Input leadingIcon="globe" value={url} onChange={e => setUrl(e.target.value)} placeholder="example.com" /></div>}
              {!cfg.hideRelationships && <div>
                <label className="bi-field__label">Relationship</label>
                <div className="bi-rel-multi">{REL.map(([id, label, cls, hint]) => <button key={id} className={"bi-rel-card bi-rel-card--" + cls + (rels.includes(id) ? " on" : "")} onClick={() => toggleRel(id)}><div className="bi-rel-card__name">{label}</div><div className="bi-rel-card__hint">{hint}</div></button>)}</div>
              </div>}
              {err && <div className="bi-hint" style={{ color: "var(--danger)" }}>{err}</div>}
              <div className="bi-hint">Manual entries are saved as <b>validated</b> immediately.</div>
            </div>
          )}
          {tab === "fetch" && phase === "idle" && (
            <div className="bi-form-grid">
              <div><label className="bi-field__label">URL to fetch</label><Input leadingIcon="link" defaultValue="https://frase.io" /></div>
              <div className="bi-hint">We'll read the page, extract a profile, and propose related entities you can review. Relationship tokens: <b>Alternative</b> · <b>Lookalike</b> · <b>Complementary</b>.</div>
            </div>
          )}
          {phase === "running" && <FetchProgress step={step} labels={labels} />}
          {phase === "results" && (
            <div style={{ paddingTop: 6 }}>
              <div className="bi-results-head"><I name="check-circle" size={16} style={{ color: "var(--success)" }} />Extracted <b style={{ fontWeight: 700 }}>Frase</b> · edit before saving</div>
              <div className="bi-form-grid" style={{ paddingTop: 0 }}>
                <div><label className="bi-field__label">Name</label><Input defaultValue="Frase" /></div>
                <div><label className="bi-field__label">Website</label><Input defaultValue="frase.io" leadingIcon="globe" /></div>
                <div><label className="bi-field__label">Relationship</label><div className="bi-rel-multi">{REL.map(([id, label, cls, hint]) => <button key={id} className={"bi-rel-card bi-rel-card--" + cls + (rels.includes(id) ? " on" : "")} onClick={() => toggleRel(id)}><div className="bi-rel-card__name">{label}</div><div className="bi-rel-card__hint">{hint}</div></button>)}</div></div>
              </div>
              <div className="bi-results-head" style={{ marginTop: 18 }}>Proposed related entities<span className="bi-conf" style={{ color: "var(--text-muted)" }}>{sel.length} selected</span></div>
              <div className="bi-proposed-list">{props.map(p => <ProposedCard key={p.id} p={p} sel={sel.includes(p.id)} onToggle={() => toggleSel(p.id)} onRel={cardRel} />)}</div>
            </div>
          )}
        </div>
      </Modal>
    );
  }

  // ---- Grow-from cross-fetch (wired to POST /bi/cross-fetch) ----
  const GROW_SRC = {
    business_profile: ["Business Profile", "briefcase"], competitor: ["Competitors", "flag"],
    industry: ["Industry", "globe"], partner: ["Partners", "git-branch"], product: ["Products", "box"], web: ["Live web", "globe"],
  };
  function GrowModal({ cfg, onClose, site, onSaved }) {
    const siteId = siteIdOf(site);
    const [web, setWeb] = useState(true);
    const [source, setSource] = useState(null);
    const [phase, setPhase] = useState("pick"); // pick | running | results
    const [step, setStep] = useState(0);
    const [proposals, setProposals] = useState([]);
    const [sel, setSel] = useState([]);
    const [webInfo, setWebInfo] = useState(null);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);
    const t = useRef(null);
    useEffect(() => () => clearInterval(t.current), []);
    const sources = ["business_profile", "competitor", "industry", "partner", "product", "web"]
      .filter(s => s !== cfg.noun).map(s => [s, GROW_SRC[s][0], GROW_SRC[s][1]]);
    const labels = ["Reading source entities", "Deriving candidates with AI", "Classifying & de-duplicating"];
    const toggleSel = (id) => setSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

    const run = async () => {
      if (!source || siteId == null) return;
      setPhase("running"); setStep(0); setErr(null);
      t.current = setInterval(() => setStep(s => Math.min(2, s + 1)), 700);
      try {
        const res = await api(`/bi/cross-fetch`, { method: "POST", body: JSON.stringify({
          site_id: siteId, target_type: cfg.noun, source, web_search: web,
        }) });
        clearInterval(t.current);
        const list = ((res && res.proposals) || []).map((p, i) => ({
          id: i, name: p.name || "", url: p.url || "", sum: p.summary || "", conf: pct(p.confidence),
          rels: (p.relationships_to_me || []).map(r => REL_IN[r]).filter(Boolean), cat: p.category || "", from: p.from || source,
        }));
        setProposals(list); setSel(list.map(p => p.id));
        setWebInfo({ grounded: !!(res && res.web_grounded), n: (res && res.web_searches) || 0 });
        setPhase("results");
      } catch (e) { clearInterval(t.current); setErr(e.message || "Grow failed"); setPhase("pick"); }
    };

    const save = async () => {
      const chosen = proposals.filter(p => sel.includes(p.id));
      if (!chosen.length || siteId == null) return;
      setSaving(true); setErr(null);
      try {
        for (const p of chosen) {
          await api(`/bi/entities`, { method: "POST", body: JSON.stringify({
            site_id: siteId, entity_type: cfg.noun, name: p.name, url: cfg.hasUrl ? (p.url || null) : null,
            data: { relationships_to_me: p.rels.map(r => REL_OUT[r]).filter(Boolean), from_source: source, from: p.from },
            source: "ai_suggested", validation_status: "pending",
          }) });
        }
        onSaved && onSaved(); onClose();
      } catch (e) { setErr(e.message || "Save failed"); setSaving(false); }
    };

    const foot = phase === "results"
      ? <><button className="bi-btn" onClick={onClose}>Cancel</button><button className="bi-btn bi-btn--primary" disabled={saving || !sel.length} onClick={save}>{saving ? "Adding…" : `Add selected (${sel.length})`}</button></>
      : <><button className="bi-btn" onClick={onClose}>Cancel</button><button className="bi-btn bi-btn--ai" disabled={!source || phase === "running"} onClick={run}><I name="download" size={16} />Grow from source</button></>;

    return (
      <Modal title={"Grow " + cfg.title.toLowerCase() + " from…"} sub="Discover new entities from a source and review each before it's tracked." onClose={onClose} foot={foot}>
        <div className="bi-modal__body">
          {err && <div className="bi-hint" style={{ color: "var(--danger)" }}>{err}</div>}
          {phase === "pick" && (
            <div style={{ paddingTop: 12 }}>
              <div className="bi-web-toggle">
                <label className="hds-switch"><input type="checkbox" checked={web} onChange={e => setWeb(e.target.checked)} /><span className="hds-switch__track"><span className="hds-switch__thumb" /></span></label>
                <div className="bi-web-toggle__meta"><div className="bi-web-toggle__t">Search the live web</div><div className="bi-web-toggle__h">More accurate &amp; current, uses more tokens.</div></div>
                <I name="globe" size={16} style={{ color: web ? "var(--info)" : "var(--text-disabled)" }} />
              </div>
              <div className="bi-cf-sources">
                {sources.map(([id, name, icon]) => <button key={id} className={"bi-cf-source" + (source === id ? " bi-cf-source--on" : "")} onClick={() => setSource(id)}><span className="bi-cf-source__icon"><I name={icon} size={16} /></span><span><div className="bi-cf-source__name">{name}</div></span></button>)}
              </div>
            </div>
          )}
          {phase === "running" && <FetchProgress step={step} labels={labels} />}
          {phase === "results" && (
            <div style={{ paddingTop: 8 }}>
              <div className="bi-results-head">{webInfo && webInfo.grounded ? <span className="bi-web-badge bi-web-badge--live"><I name="globe" size={12} />{webInfo.n} live web searches</span> : <span className="bi-web-badge bi-web-badge--quick">quick · no web search</span>}<span style={{ marginLeft: "auto", color: "var(--text-muted)", fontWeight: 400 }}>{proposals.length} new</span></div>
              {proposals.length === 0
                ? <div className="bi-hint">No new entities found from this source. Try the live web, or a different source.</div>
                : <div className="bi-proposed-list">{proposals.map(p => <ProposedCard key={p.id} p={p} sel={sel.includes(p.id)} onToggle={() => toggleSel(p.id)} onRel={() => {}} />)}</div>}
            </div>
          )}
        </div>
      </Modal>
    );
  }

  // ---- shared dialogs (1K) ----
  function ConfirmDialog({ danger, title, body, confirmLabel, onClose, onConfirm }) {
    const done = () => { if (onConfirm) onConfirm(); else onClose(); };
    return (
      <Modal sm onClose={onClose} title="" foot={<><button className="bi-btn" onClick={onClose}>Cancel</button><button className={"bi-btn " + (danger ? "bi-btn--danger" : "bi-btn--primary")} onClick={done} style={danger ? { background: "var(--danger)", borderColor: "var(--danger)", color: "#fff" } : null}>{confirmLabel}</button></>}>
        <div className="bi-modal__body" style={{ display: "flex", gap: 14, paddingTop: 4, paddingBottom: 8 }}>
          <span className={"bi-confirm-icon bi-confirm-icon--" + (danger ? "danger" : "primary")}><I name={danger ? "trash" : "sparkles"} size={22} /></span>
          <div><div className="bi-modal__title" style={{ fontSize: "var(--text-md)" }}>{title}</div><div className="bi-modal__sub">{body}</div></div>
        </div>
      </Modal>
    );
  }
  function AlertDialog({ onClose }) {
    return (
      <Modal sm onClose={onClose} title="" foot={<button className="bi-btn bi-btn--primary" onClick={onClose}>OK</button>}>
        <div className="bi-modal__body" style={{ display: "flex", gap: 14, paddingTop: 4, paddingBottom: 8 }}>
          <span className="bi-confirm-icon bi-confirm-icon--primary"><I name="info" size={22} /></span>
          <div><div className="bi-modal__title" style={{ fontSize: "var(--text-md)" }}>Set a market first</div><div className="bi-modal__sub">BI suggestions are constrained to a single market. Set one in your Business Profile to grow entities.</div></div>
        </div>
      </Modal>
    );
  }
  function ShareModal({ onClose, site, sites, entity, onSaved }) {
    const siteId = siteIdOf(site);
    const dests = (sites || []).filter(s => s.id !== (site && site.id));
    const [sel, setSel] = useState([]);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);
    const all = dests.length > 0 && sel.length === dests.length;
    const toggle = (id) => setSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
    const send = async () => {
      if (!entity || !sel.length || siteId == null) return;
      setSaving(true); setErr(null);
      try {
        await api(`/bi/entities/${entity.id}/share`, { method: "POST", body: JSON.stringify({ site_id: siteId, dest_site_ids: sel, from_label: (site && site.name) || "" }) });
        onSaved && onSaved(); onClose();
      } catch (e) { setErr(e.message || "Share failed"); setSaving(false); }
    };
    return (
      <Modal title="Share to a site" sub="The entity lands in that site's Inbox for review before it's tracked there." onClose={onClose} foot={<><button className="bi-btn" onClick={onClose}>Cancel</button><button className="bi-btn bi-btn--primary" disabled={saving || !sel.length} onClick={send}><I name="share" size={16} />{saving ? "Sending…" : `Send (${sel.length})`}</button></>}>
        <div className="bi-modal__body" style={{ paddingTop: 8 }}>
          {err && <div className="bi-hint" style={{ color: "var(--danger)" }}>{err}</div>}
          {dests.length === 0
            ? <div className="bi-hint">No other sites to share to.</div>
            : <>
              <label className="bi-checkrow" style={{ borderBottom: "1px solid var(--border-subtle)", borderRadius: 0 }} onClick={() => setSel(all ? [] : dests.map(s => s.id))}><span className="hds-check"><input type="checkbox" readOnly checked={all} /><span className="hds-check__box"><I name="check" size={12} /></span></span><span className="bi-checkrow__name" style={{ fontWeight: 600 }}>Select all sites</span></label>
              <div className="bi-checklist">{dests.map(s => <label className="bi-checkrow" key={s.id} onClick={() => toggle(s.id)}><span className="hds-check"><input type="checkbox" readOnly checked={sel.includes(s.id)} /><span className="hds-check__box"><I name="check" size={12} /></span></span><span className="bi-checkrow__name">{s.name}</span><span className="bi-checkrow__sub">{s.url}</span></label>)}</div>
            </>}
        </div>
      </Modal>
    );
  }
  function VariantModal({ onClose }) {
    const DS = window.HuddleDesignSystem_775113;
    const { Input, Select } = DS;
    const [type, setType] = useState("geographic");
    return (
      <Modal title="Add a sub-product" sub="Create a variant that rolls up to its parent." onClose={onClose} foot={<><button className="bi-btn" onClick={onClose}>Cancel</button><button className="bi-btn bi-btn--primary" onClick={onClose}>Add variant</button></>}>
        <div className="bi-modal__body"><div className="bi-form-grid">
          <div><label className="bi-field__label">Variant type</label><Select value={type} onChange={e => setType(e.target.value)}><option value="geographic">Geographic</option><option value="spec">Specialization</option><option value="custom">Custom</option></Select></div>
          {type === "geographic"
            ? <div><label className="bi-field__label">Country</label><Select defaultValue="fr"><option value="fr">France</option><option value="de">Germany</option><option value="es">Spain</option></Select></div>
            : <div><label className="bi-field__label">Label</label><Input placeholder={type === "spec" ? "e.g. Enterprise tier" : "Custom label"} /></div>}
          <div><label className="bi-field__label">Name <span className="bi-field__req">*</span></label><Input placeholder="Variant name" /></div>
        </div></div>
      </Modal>
    );
  }

  window.MlBiModals = { _Modal: Modal, AddEditModal, GrowModal, ConfirmDialog, AlertDialog, ShareModal, VariantModal, ProviderModal, AddOfferingsModal };

  function ProviderModal({ onClose }) {
    const DS = window.HuddleDesignSystem_775113; const { Input, Select } = DS;
    const [kind, setKind] = useState("comp");
    return (
      <Modal title="Add a provider" sub="Who else offers this - with their pricing and source." onClose={onClose} foot={<><button className="bi-btn" onClick={onClose}>Cancel</button><button className="bi-btn bi-btn--primary" onClick={onClose}>Add provider</button></>}>
        <div className="bi-modal__body"><div className="bi-form-grid">
          <div><label className="bi-field__label">Who offers it</label><div className="bi-rel-multi" style={{ gridTemplateColumns: "repeat(4,1fr)" }}>{[["us", "★ Us"], ["comp", "Competitor"], ["partner", "Partner"], ["market", "Market"]].map(([id, l]) => <button key={id} className={"bi-rel-card" + (kind === id ? " on" : "")} style={kind === id ? { borderColor: "var(--primary)", background: "var(--primary-tint)" } : null} onClick={() => setKind(id)}><div className="bi-rel-card__name" style={{ fontSize: 12 }}>{l}</div></button>)}</div></div>
          <div><label className="bi-field__label">Name <span className="bi-field__req">*</span></label><Input placeholder="e.g. Stripe Atlas" /></div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div><label className="bi-field__label">Website</label><Input leadingIcon="globe" placeholder="example.com" /></div>
            <div><label className="bi-field__label">Pricing</label><Input placeholder="€199 / mo" /></div>
          </div>
        </div></div>
      </Modal>
    );
  }

  function AddOfferingsModal({ onClose }) {
    const DS = window.HuddleDesignSystem_775113; const { Input, Select } = DS;
    const [src, setSrc] = useState("site");
    const [phase, setPhase] = useState("pick"); // pick | running | results
    const [step, setStep] = useState(0);
    const [sel, setSel] = useState([1, 2, 3]);
    const t = useRef(null);
    useEffect(() => () => clearInterval(t.current), []);
    const sources = [["site", "My website", "globe"], ["comp", "A tracked competitor", "flag"], ["partner", "A tracked partner", "git-branch"], ["url", "Another URL", "link"]];
    const labels = ["Reading the page", "Extracting offerings with AI", "Matching to service lines"];
    const found = [{ id: 1, name: "VAT registration", price: "€149", sum: "Fixed-fee VAT registration.", sl: "tax" }, { id: 2, name: "Annual filing", price: "€99/yr", sum: "Recurring statutory filing.", sl: "compliance" }, { id: 3, name: "Registered agent", price: "€50/yr", sum: "Local registered-agent service.", sl: "compliance" }];
    const run = () => { setPhase("running"); setStep(0); t.current = setInterval(() => setStep(s => { if (s >= 2) { clearInterval(t.current); setTimeout(() => setPhase("results"), 500); return s; } return s + 1; }), 850); };
    const toggle = id => setSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
    const foot = phase === "results"
      ? <><button className="bi-btn" onClick={onClose}>Cancel</button><button className="bi-btn bi-btn--primary" onClick={onClose}>Add selected ({sel.length})</button></>
      : <><button className="bi-btn" onClick={onClose}>Cancel</button><button className="bi-btn bi-btn--ai" disabled={phase === "running"} onClick={run}><I name="sparkles" size={16} />Read offerings</button></>;
    return (
      <Modal title="Add offerings from a source" sub="Read offerings from a page and merge them by name + service line." onClose={onClose} foot={foot}>
        <div className="bi-modal__body">
          {phase === "pick" && <div style={{ paddingTop: 12 }}>
            <div className="bi-cf-sources">{sources.map(([id, name, icon]) => <button key={id} className={"bi-cf-source" + (src === id ? " bi-cf-source--on" : "")} onClick={() => setSrc(id)}><span className="bi-cf-source__icon"><I name={icon} size={16} /></span><span className="bi-cf-source__name">{name}</span></button>)}</div>
            <div style={{ marginTop: 12 }}><label className="bi-field__label">Page URL</label><Input leadingIcon="link" defaultValue="https://marketlens.com/services" /></div>
          </div>}
          {phase === "running" && <FetchProgress step={step} labels={labels} />}
          {phase === "results" && <div style={{ paddingTop: 8 }}>
            <div className="bi-results-head"><I name="check-circle" size={16} style={{ color: "var(--success)" }} />Found 3 offerings · edit service lines before adding</div>
            <div className="bi-proposed-list">{found.map(f => (
              <div className={"bi-proposed-card" + (sel.includes(f.id) ? " bi-proposed-card--on" : "")} key={f.id} onClick={() => toggle(f.id)}>
                <label className="hds-check" onClick={e => e.stopPropagation()}><input type="checkbox" checked={sel.includes(f.id)} onChange={() => toggle(f.id)} /><span className="hds-check__box"><I name="check" size={12} /></span></label>
                <div className="bi-proposed-card__main"><div className="bi-proposed-card__name">{f.name}<span className="bi-conf" style={{ color: "var(--text-primary)" }}>{f.price}</span></div><div className="bi-proposed-card__sum">{f.sum}</div>
                  <div style={{ marginTop: 8 }} onClick={e => e.stopPropagation()}><Select defaultValue={f.sl} style={{ height: 30, fontSize: 12, maxWidth: 200 }}><option value="formation">Company formation</option><option value="tax">Tax &amp; VAT</option><option value="compliance">Compliance</option></Select></div>
                </div>
              </div>
            ))}</div>
          </div>}
        </div>
      </Modal>
    );
  }
})();
